import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterValues, notesFiltersInitialValues } from '../../../screens/NotesPage/Filters';
import { GroupedMessageSet } from '../../../screens/NotesPage/useConversations';

export interface NotesState {
  conversationDocumentMetadata: {
    entityFullName: string;
    humanReadableObjectReferenceId: string;
    objectType: MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum;
    systemObjectId: string;
  } | null;
  conversations: Array<MilesPosNotesIntegrationService.Components.Schemas.Conversation>;
  currentConversation: MilesPosNotesIntegrationService.Components.Schemas.Conversation | null;
  currentConversationMessages: GroupedMessageSet[];
  filters: FilterValues;
  intent: { type: 'READ'; conversationId: string | null } | { type: 'CREATE_CONVERSATION' };
  isSendingMessage: boolean;
  isFetchingConversationsLoading: boolean;
  lastConversationFetchError: string | null;
}

const initialNotesState: NotesState = {
  conversationDocumentMetadata: null,
  conversations: [],
  currentConversation: null,
  currentConversationMessages: [],
  filters: notesFiltersInitialValues,
  intent: { type: 'READ', conversationId: null },
  isSendingMessage: false,
  isFetchingConversationsLoading: false,
  lastConversationFetchError: null,
};

export const slice = createSlice({
  name: 'notes',
  initialState: initialNotesState,
  selectors: {
    conversationDocumentMetadata(state) {
      return state.conversationDocumentMetadata;
    },
    conversations(state) {
      return state.conversations;
    },
    currentConversation(state) {
      return state.currentConversation;
    },
    currentConversationMessages(state) {
      return state.currentConversationMessages;
    },
    filters(state) {
      return state.filters;
    },
    intent(state) {
      return state.intent;
    },
    isSendingMessage(state) {
      return state.isSendingMessage;
    },
    isFetchingConversationsLoading(state) {
      return state.isFetchingConversationsLoading;
    },
    lastConversationFetchError(state) {
      return state.lastConversationFetchError;
    },
  },
  reducers: {
    setConversationDocumentMetadata(state, action: PayloadAction<NotesState['conversationDocumentMetadata']>) {
      state.conversationDocumentMetadata = action.payload;
    },
    setConversations(state, action: PayloadAction<NotesState['conversations']>) {
      state.conversations = action.payload;
    },
    setCurrentConversation(state, action: PayloadAction<NotesState['currentConversation']>) {
      state.currentConversation = action.payload;
    },
    setCurrentConversationMessages(state, action: PayloadAction<NotesState['currentConversationMessages']>) {
      state.currentConversationMessages = action.payload;
    },
    setFilters(state, action: PayloadAction<NotesState['filters']>) {
      state.filters = action.payload;
    },
    setIntent(state, action: PayloadAction<NotesState['intent']>) {
      state.intent = action.payload;
    },
    setIsSendingMessage(state, action: PayloadAction<boolean>) {
      state.isSendingMessage = action.payload;
    },
    setIsFetchingConversationsLoading(state, action: PayloadAction<boolean>) {
      state.isFetchingConversationsLoading = action.payload;
    },
    setLastConversationFetchError(state, action: PayloadAction<string | null>) {
      state.lastConversationFetchError = action.payload;
    },
    resetNotes() {
      return initialNotesState;
    },
    unloadCurrentConversation(state) {
      return {
        ...state,
        conversationDocumentMetadata: null,
        currentConversation: null,
        currentConversationMessages: [],
      };
    },
  },
});

export const {
  setConversations,
  setCurrentConversation,
  setCurrentConversationMessages,
  setFilters,
  setIntent,
  resetNotes,
  setIsSendingMessage,
  setIsFetchingConversationsLoading,
  setConversationDocumentMetadata,
  unloadCurrentConversation,
  setLastConversationFetchError,
} = slice.actions;

export const notesSelectors = slice.selectors;
export const notesSlice = slice;
